import type { TooltipContentProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { type ChartTone, TONE_SWATCH } from './chart-colors.js';

export interface TooltipSeries {
  key: string;
  label: string;
  tone: ChartTone;
}

interface ChartTooltipCardProps extends Partial<TooltipContentProps<ValueType, NameType>> {
  series: readonly TooltipSeries[];
  formatLabel?: (label: string) => string;
  formatValue?: (value: number) => string;
}

/** The hover card of every chart (NextAdmin chart tooltip): the x label, then one row per series. */
export function ChartTooltipCard({ active, payload, label, series, formatLabel, formatValue }: ChartTooltipCardProps) {
  if (!active || !payload?.length) return null;
  const heading = label === undefined || label === null ? '' : String(label);
  return (
    <div className="min-w-32 space-y-1.5 rounded-xl border border-card-border bg-card-background p-3 shadow-md">
      {heading && (
        <p className="mb-1 border-b border-card-border pb-1 text-xs font-semibold text-text-primary">
          {formatLabel ? formatLabel(heading) : heading}
        </p>
      )}
      {payload.map((item) => {
        const key = String(item.dataKey ?? item.name ?? '');
        const meta = series.find((s) => s.key === key);
        const value = typeof item.value === 'number' ? item.value : Number(item.value);
        return (
          <div key={key} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-text-secondary">
              <span className={`size-1.5 rounded-full ${TONE_SWATCH[meta?.tone ?? 'accent']}`} aria-hidden="true" />
              {meta?.label ?? key}
            </span>
            <span className="font-semibold text-text-primary tabular-nums">
              {Number.isFinite(value)
                ? formatValue
                  ? formatValue(value)
                  : value.toLocaleString()
                : String(item.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
